import { t } from "i18next";
import {
  getCachedLanguageCodeFromIndexedDB,
  getCachedVoiceFromIndexedDB,
  getEnglishPronunciationFromLocalStorage,
  getSpeakingRateFromLocalStorage,
  getVoiceGenderFromLocalStroage,
  setCachedVoiceToIndexedDB,
} from "../../settings/util/voiceSettings";
import { detactLanguageCode } from "../service/languageApi";
import { SupportedLanguages } from "./languages";
import { fetchAudio } from "../service/voiceApi";
import { getSpeakingRateValue } from "../../settings/util/speakingRateHelper";

export enum VoiceGender {
  male = "male",
  female = "female",
}

export enum EnglishPronunciation {
  america = "america",
  england = "england",
  australia = "australia",
}

export function getGenderString(gender: VoiceGender): string {
  switch (gender) {
    case VoiceGender.male:
      return t("male");
    case VoiceGender.female:
      return t("female");
  }
}

export function getEnglishPronunciationString(
  pronunciation: EnglishPronunciation
): string {
  switch (pronunciation) {
    case EnglishPronunciation.america:
      return t("american");
    case EnglishPronunciation.england:
      return t("english");
    case EnglishPronunciation.australia:
      return t("australia");
  }
}

export async function getVoiceFile(
  text: string,
  languageCode: string
): Promise<string> {
  const speakingRate = getSpeakingRateFromLocalStorage();
  const speakingRateValue = getSpeakingRateValue(speakingRate);

  try {
    let lang: string;
    let voiceType: string;

    if (languageCode.length === 0) {
      const cachedLangage = await getCachedLanguageCodeFromIndexedDB(text);

      lang =
        cachedLangage !== null ? cachedLangage : await detactLanguageCode(text);

      voiceType = getVoiceActor(lang);
    } else {
      lang = languageCode;
      voiceType = getVoiceActor(lang);
    }

    const cachedVoice = await getCachedVoiceFromIndexedDB(
      text,
      languageCode,
      voiceType,
      speakingRateValue
    );

    if (cachedVoice !== null) {
      console.log("use cached");
      return cachedVoice;
    }
    console.log("request api");

    const audio = await fetchAudio(
      text,
      lang,
      getVoiceActor(lang),
      speakingRateValue
    );
    setCachedVoiceToIndexedDB(
      text,
      languageCode,
      voiceType,
      audio,
      speakingRateValue
    );
    return audio;
  } catch (error) {
    return Promise.reject(error);
  }
}

// 블루투스 이어폰 등 출력 장치가 깨어나는 동안 앞부분이 잘리므로 무음을 앞에 붙인다
const LEADING_SILENCE_MS = 200;

export function makeBase64String(src: string): string {
  let str = "data:audio/wav;base64,";
  str += addLeadingSilence(src, LEADING_SILENCE_MS);
  return str;
}

function addLeadingSilence(base64Wav: string, durationMs: number): string {
  try {
    const binary = atob(base64Wav);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    const view = new DataView(bytes.buffer);
    const readTag = (offset: number) =>
      String.fromCharCode(...Array.from(bytes.subarray(offset, offset + 4)));

    if (readTag(0) !== "RIFF" || readTag(8) !== "WAVE") {
      return base64Wav;
    }

    let blockAlign = 0;
    let byteRate = 0;
    let offset = 12;
    while (offset + 8 <= bytes.length) {
      const tag = readTag(offset);
      const size = view.getUint32(offset + 4, true);

      if (tag === "fmt ") {
        byteRate = view.getUint32(offset + 16, true);
        blockAlign = view.getUint16(offset + 20, true);
      } else if (tag === "data") {
        if (blockAlign === 0) {
          return base64Wav;
        }
        const silenceSize =
          Math.round((byteRate * durationMs) / 1000 / blockAlign) * blockAlign;
        const dataStart = offset + 8;
        // 16bit PCM의 무음은 0이라 새 배열의 기본값을 그대로 쓴다
        const padded = new Uint8Array(bytes.length + silenceSize);
        padded.set(bytes.subarray(0, dataStart), 0);
        padded.set(bytes.subarray(dataStart), dataStart + silenceSize);

        const paddedView = new DataView(padded.buffer);
        paddedView.setUint32(4, view.getUint32(4, true) + silenceSize, true);
        paddedView.setUint32(offset + 4, size + silenceSize, true);

        let result = "";
        for (let i = 0; i < padded.length; i += 0x8000) {
          result += String.fromCharCode(
            ...Array.from(padded.subarray(i, i + 0x8000))
          );
        }
        return btoa(result);
      }
      offset += 8 + size + (size % 2);
    }
    return base64Wav;
  } catch {
    return base64Wav;
  }
}

export function getVoiceActor(languageCode: string): string {
  const gender = getVoiceGenderFromLocalStroage();
  const englishPronunciation = getEnglishPronunciationFromLocalStorage();

  switch (languageCode) {
    case SupportedLanguages.en:
      switch (gender) {
        case VoiceGender.male:
          switch (englishPronunciation) {
            case EnglishPronunciation.america:
              return "en-US-Wavenet-J";
            case EnglishPronunciation.england:
              return "en-GB-Wavenet-D";
            case EnglishPronunciation.australia:
              return "en-AU-Wavenet-D";
          }
          break;
        case VoiceGender.female:
          switch (englishPronunciation) {
            case EnglishPronunciation.america:
              return "en-US-Wavenet-H";
            case EnglishPronunciation.england:
              return "en-GB-Wavenet-F";
            case EnglishPronunciation.australia:
              return "en-AU-Wavenet-C";
          }
      }
      break;
    case SupportedLanguages.da:
      switch (gender) {
        case VoiceGender.male:
          return "da-DK-Wavenet-A";
        case VoiceGender.female:
          return "da-DK-Wavenet-A";
      }
      break;
    case SupportedLanguages.nl:
      switch (gender) {
        case VoiceGender.male:
          return "nl-NL-Wavenet-B";
        case VoiceGender.female:
          return "nl-NL-Wavenet-A";
      }
      break;
    case SupportedLanguages.fr:
      switch (gender) {
        case VoiceGender.male:
          return "fr-FR-Wavenet-D";
        case VoiceGender.female:
          return "fr-FR-Wavenet-E";
      }
      break;
    case SupportedLanguages.de:
      switch (gender) {
        case VoiceGender.male:
          return "de-DE-Wavenet-E";
        case VoiceGender.female:
          return "de-DE-Wavenet-F";
      }
      break;
    case SupportedLanguages.it:
      switch (gender) {
        case VoiceGender.male:
          return "it-IT-Wavenet-B";
        case VoiceGender.female:
          return "it-IT-Wavenet-A";
      }
      break;
    case SupportedLanguages.ja:
      switch (gender) {
        case VoiceGender.male:
          return "ja-JP-Wavenet-D";
        case VoiceGender.female:
          return "ja-JP-Wavenet-B";
      }
      break;
    case SupportedLanguages.ko:
      switch (gender) {
        case VoiceGender.male:
          return "ko-KR-Wavenet-D";
        case VoiceGender.female:
          return "ko-KR-Wavenet-B";
      }
      break;
    case SupportedLanguages.nb:
      switch (gender) {
        case VoiceGender.male:
          return "nb-NO-Wavenet-D";
        case VoiceGender.female:
          return "nb-no-Wavenet-E";
      }
      break;
    case SupportedLanguages.pl:
      switch (gender) {
        case VoiceGender.male:
          return "pl-PL-Wavenet-C";
        case VoiceGender.female:
          return "pl-PL-Wavenet-E";
      }
      break;
    case SupportedLanguages.pt:
      switch (gender) {
        case VoiceGender.male:
          return "pt-PT-Wavenet-C";
        case VoiceGender.female:
          return "pt-PT-Wavenet-D";
      }
      break;
    case SupportedLanguages.ru:
      switch (gender) {
        case VoiceGender.male:
          return "ru-RU-Wavenet-D";
        case VoiceGender.female:
          return "ru-RU-Wavenet-E";
      }
      break;
    case SupportedLanguages.sk:
      switch (gender) {
        case VoiceGender.male:
          return "sk-SK-Wavenet-A";
        case VoiceGender.female:
          return "sk-SK-Wavenet-A";
      }
      break;
    case SupportedLanguages.es:
      switch (gender) {
        case VoiceGender.male:
          return "es-ES-Standard-A";
        case VoiceGender.female:
          return "es-ES-Standard-A";
      }
      break;
    case SupportedLanguages.sv:
      switch (gender) {
        case VoiceGender.male:
          return "sv-SE-Wavenet-A";
        case VoiceGender.female:
          return "sv-SE-Wavenet-A";
      }
      break;
    case SupportedLanguages.tr:
      switch (gender) {
        case VoiceGender.male:
          return "tr-TR-Wavenet-E";
        case VoiceGender.female:
          return "tr-TR-Wavenet-D";
      }
      break;
    case SupportedLanguages.uk:
      switch (gender) {
        case VoiceGender.male:
          return "uk-UA-Wavenet-A";
        case VoiceGender.female:
          return "uk-UA-Wavenet-A";
      }
      break;
    case SupportedLanguages.zh:
      switch (gender) {
        case VoiceGender.male:
          return "cmn-CN-Wavenet-C";
        case VoiceGender.female:
          return "cmn-CN-Wavenet-D";
      }
      break;
    case SupportedLanguages.ar:
      switch (gender) {
        case VoiceGender.male:
          return "ar-XA-Wavenet-C";
        case VoiceGender.female:
          return "ar-XA-Wavenet-A";
      }
      break;
    case SupportedLanguages.cs:
      switch (gender) {
        case VoiceGender.male:
          return "cs-CZ-Wavenet-A";
        case VoiceGender.female:
          return "cs-CZ-Wavenet-A";
      }
      break;
    case SupportedLanguages.hi:
      switch (gender) {
        case VoiceGender.male:
          return "hi-IN-Wavenet-C";
        case VoiceGender.female:
          return "hi-IN-Wavenet-D";
      }
      break;
    case SupportedLanguages.hu:
      switch (gender) {
        case VoiceGender.male:
          return "hu-HU-Wavenet-A";
        case VoiceGender.female:
          return "hu-HU-Wavenet-A";
      }
      break;
    case SupportedLanguages.id:
      switch (gender) {
        case VoiceGender.male:
          return "id-ID-Wavenet-C";
        case VoiceGender.female:
          return "id-ID-Wavenet-D";
      }
      break;
    case SupportedLanguages.vi:
      switch (gender) {
        case VoiceGender.male:
          return "vi-VN-Wavenet-C";
        case VoiceGender.female:
          return "vi-VN-Wavenet-D";
      }
      break;
    case SupportedLanguages.bn:
      switch (gender) {
        case VoiceGender.male:
          return "bn-IN-Wavenet-D";
        case VoiceGender.female:
          return "bn-IN-Wavenet-C";
      }
      break;
    case SupportedLanguages.fil:
      switch (gender) {
        case VoiceGender.male:
          return "fil-PH-Wavenet-D";
        case VoiceGender.female:
          return "fil-PH-Wavenet-B";
      }
      break;
    case SupportedLanguages.fi:
      switch (gender) {
        case VoiceGender.male:
          return "fi-FI-Standard-A";
        case VoiceGender.female:
          return "fi-FI-Standard-A";
      }
      break;
    case SupportedLanguages.el:
      switch (gender) {
        case VoiceGender.male:
          return "el-GR-Wavenet-A";
        case VoiceGender.female:
          return "el-GR-Wavenet-A";
      }
      break;
    case SupportedLanguages.gu:
      switch (gender) {
        case VoiceGender.male:
          return "gu-IN-Wavenet-B";
        case VoiceGender.female:
          return "gu-IN-Wavenet-A";
      }
      break;
    case SupportedLanguages.he:
      switch (gender) {
        case VoiceGender.male:
          return "he-IL-Wavenet-D";
        case VoiceGender.female:
          return "he-IL-Wavenet-C";
      }
      break;
    case SupportedLanguages.kn:
      switch (gender) {
        case VoiceGender.male:
          return "kn-IN-Wavenet-D";
        case VoiceGender.female:
          return "kn-IN-Wavenet-C";
      }
      break;
    case SupportedLanguages.ms:
      switch (gender) {
        case VoiceGender.male:
          return "ms-MY-Wavenet-D";
        case VoiceGender.female:
          return "ms-MY-Wavenet-C";
      }
      break;
    case SupportedLanguages.ml:
      switch (gender) {
        case VoiceGender.male:
          return "ml-IN-Wavenet-D";
        case VoiceGender.female:
          return "ml-IN-Wavenet-C";
      }
      break;
    case SupportedLanguages.mr:
      switch (gender) {
        case VoiceGender.male:
          return "mr-IN-Wavenet-B";
        case VoiceGender.female:
          return "mr-IN-Wavenet-C";
      }
      break;
    case SupportedLanguages.pa:
      switch (gender) {
        case VoiceGender.male:
          return "pa-IN-Wavenet-D";
        case VoiceGender.female:
          return "pa-IN-Wavenet-C";
      }
      break;
    case SupportedLanguages.ro:
      switch (gender) {
        case VoiceGender.male:
          return "ro-RO-Wavenet-A";
        case VoiceGender.female:
          return "ro-RO-Wavenet-A";
      }
      break;
    case SupportedLanguages.ta:
      switch (gender) {
        case VoiceGender.male:
          return "ta-IN-Wavenet-D";
        case VoiceGender.female:
          return "ta-IN-Wavenet-C";
      }
      break;
    default:
      break;
  }
  // 성별 · 발음 값은 저장소에서 읽을 때 검증되므로 여기에는 지원하지 않는 언어만 도달한다
  throw new Error("음성을 지원하지 않는 언어입니다.");
}
